import cloneDeep from 'lodash/cloneDeep';

import { ActionTree, MutationTree } from 'vuex';
import { RootState } from '@/store/types';
import { ServerResult } from '@/core/http/server_result';
import { fetchEnumsFromSchema } from '@/core/tools/forms/fetch_enums_from_schema';
import { generateFormObject } from '@/core/tools/forms/generate_form_object';
import { prepareFormObjectForSave } from '@/core/tools/forms/prepare_form_object_for_save';
import moment from 'moment';

interface ClientPriceElementFormState {
    loaded: boolean;
    loading: boolean;
    fatalError: boolean;

    document: any;
    form: any;
    product_rels: any[];
    targetForDelete: any[];
    schema: any;

    meta: any;
}

const state: ClientPriceElementFormState = {
    loading: false,
    loaded: false,
    fatalError: false,

    document: null,
    product_rels: [],
    targetForDelete: [],
    form: null,
    schema: null,

    meta: {},
};

const initState: ClientPriceElementFormState = cloneDeep(state);

export const PRICE_ITEM_DATA = 'GET_PRICE_ITEM_DATA';
export const PRICE_ITEM_RELS_DATA = 'GET_PRICE_ITEM_RELS_DATA';
export const PRICE_ITEM_SCHEMA = 'GET_PRICE_ITEM_SCHEMA';

export const actions: ActionTree<ClientPriceElementFormState, RootState> = {
    reset({ commit }) {
        commit('RESET_STATE')
    },

    init({ commit, dispatch }, params) {
        return dispatch('loadData', params);
    },

    loadData({ commit }, params) {
        const vm = (this as any)._vm;

        return new Promise((resolve, reject) => {
            commit('START_LOADING');

            vm.$db([
                vm.$q(PRICE_ITEM_DATA)
                    .raw('documents.get')
                    .action({
                        id: params.id
                    }),
                vm.$q(PRICE_ITEM_RELS_DATA)
                    .raw('documents.list')
                    .action({
                    columns: {
                        id: 'cpcr.id',
                        client_product_container_id: 'cpcr.client_product_container_id',
                        client_product_container_rel_id: 'cpcr.client_product_container_rel_id',
                        name: {
                        $case: [
                            [
                            { $eq: [`cpcr.client_product_container_id`, { $: params.id }] },
                            {$concat: [`p2.name`, {$: ' '}, 'pc2.name']},
                            ],
                            [
                            { $eq: [`cpcr.client_product_container_rel_id`, { $: params.id }] },
                            {$concat: [`p.name`, {$: ' '}, 'pc.name']},
                            ],
        
                        ]
                        }
                    },
                    from: `s:client_product_containers_rels`,
                    from_as: 'cpcr',
                    joins: [
                        {
                            join: 's:client_product_containers',
                            as: 'cpc',
                            on: {
                                $eq: ['cpc.id', 'cpcr.client_product_container_id']
                            }
                        },
                        {
                            join: 's:product_containers',
                            as: 'pc',
                            on: {
                                $eq: ['pc.id', 'cpc.product_container_id']
                            }
                        },
                        {
                            join: 's:products',
                            as: 'p',
                            on: {
                                $eq: ['p.id', 'pc.product_id']
                            }
                        },
                        {
                            join: 's:client_product_containers',
                            as: 'cpc2',
                            on: {
                                $eq: ['cpc2.id', 'cpcr.client_product_container_rel_id']
                            }
                        },
                        {
                            join: 's:product_containers',
                            as: 'pc2',
                            on: {
                                $eq: ['pc2.id', 'cpc2.product_container_id']
                            }
                        },
                        {
                            join: 's:products',
                            as: 'p2',
                            on: {
                                $eq: ['p2.id', 'pc2.product_id']
                            }
                        },
                    ],
                    where: {
                        $eq: [`cpcr.client_product_container_id`, { $: params.id }]
                    }
                }),
            ]).then((result: ServerResult) => {
                commit('SET_FORM_DATA', {
                    ...result.getOperation(PRICE_ITEM_DATA).getContent(),
                    product_rels: result.getOperation(PRICE_ITEM_RELS_DATA).getContent().documents
                });

                commit('LOADED_SUCCESS');
                resolve();
            })
            .catch((error: any) => {
                console.log('error', error)
                commit('LOADED_ERROR');
                reject()
            })
        })
    },

    prepareNew({ commit, state }, initParams) {
        const vm = (this as any)._vm;

        return new Promise((resolve, reject) => {
            commit('START_LOADING');

            vm.$db([
                vm.$q(PRICE_ITEM_SCHEMA)
                    .raw('schemas.get')
                    .action({
                        id: `s:client_product_containers`
                    }),
            ]).then((result: ServerResult) => {
                commit('GENERATE_FORM_DATA', {
                    ...result.getOperation(PRICE_ITEM_SCHEMA).getContent(),
                    initParams
                });

                commit('LOADED_SUCCESS');
                resolve();
            })
            .catch((error: any) => {
                console.log('error', error)
                commit('LOADED_ERROR');
                reject()
            })
        })
    },

    saveForm({ commit }, documentId) {
        const vm = (this as any)._vm;

        const normalizedForm = prepareFormObjectForSave(state.schema, state.form);
        const products = state.product_rels.map((item: any) => {
            return {
                id: item.id,
                client_product_container_rel_id: item.client_product_container_rel_id
            }
        });

        return new Promise((resolve, reject) => {
            commit('START_LOADING');

            let saveQuery = null;

            if (documentId) {
                saveQuery = vm.$q('PRICE_ITEM_DATA_SAVE')
                    .update(documentId, normalizedForm)
                    .createManyRelation('client_product_containers_rels', 'client_product_container_id', (createRelated: any) => {
                      return products.filter((item: any) => !item.id || (typeof (item.id) !== 'string')).map((item, index) => createRelated(item));
                    })
                    .deleteManyRelation('client_product_containers_rels', 'client_product_container_id', (deleteRelated: any) => {
                      return state.targetForDelete.map((item: any, index) => deleteRelated({id: item}));
                    })
            } else {
                saveQuery = vm.$q('PRICE_ITEM_DATA_SAVE')
                    .create('s:client_product_containers', normalizedForm)
                    .withManyRelation('client_product_containers_rels', 'client_product_container_id', (createRelated: any) => {
                      return products.map((item: any) => createRelated(item));
                    })
            }

            vm.$db([
                saveQuery
            ]).then((result: ServerResult) => {
                const operationResult = result.getOperation('PRICE_ITEM_DATA_SAVE');

                if (operationResult.hasError()) {
                    console.log('error', operationResult.getContent());

                    commit('LOADED_SUCCESS');
                    reject();
                } else {
                    commit('ON_FORM_SAVE', operationResult.getContent())

                    commit('LOADED_SUCCESS');
                    resolve(operationResult.getContent());
                }
            })
            .catch((error: any) => {
                console.error('errror', error)
                reject(error);
            });
        })
    },

    changeFormValue({commit}, {field, newValue}) {
        commit('CHANGE_VALUE', {field, newValue});
    },

    addProduct({ commit }, product) {
      commit('ADD_PRODUCT', product);
    },
  
    removeProduct({ commit }, index: number) {
      commit('REMOVE_PRODUCT', index);
    },

};

export const mutations: MutationTree<ClientPriceElementFormState> = {
    RESET_STATE: (state) => {
        for(const key in initState) {
            (state as any)[key] = (initState as any)[key]
        }
    },

    START_LOADING: (state) => {
        state.loaded = false;
        state.loading = true;
        state.fatalError = false;
    },

    LOADED_SUCCESS: (state) => {
        state.loaded = true;
        state.loading = false;
        state.fatalError = false;
    },

    LOADED_ERROR: (state) => {
        state.loaded = true;
        state.loading = false;
        state.fatalError = true;
    },

    SET_FORM_DATA(state, {document, schema, product_rels}) {
        state.document = document;
        state.schema = schema;
        state.meta = fetchEnumsFromSchema(schema);

        state.form = generateFormObject(schema, state.document);
        state.product_rels = product_rels
    },

    GENERATE_FORM_DATA(state, {schema, initParams}) {
        state.document = {};
        state.schema = schema;
        state.meta = fetchEnumsFromSchema(schema);

        state.form = generateFormObject(schema, initParams, true);
    },

    ON_FORM_SAVE(state, {document, schema}) {
        state.document = document;
        state.schema = schema;

        state.form = generateFormObject(schema, state.document);
    },

    CHANGE_VALUE(state: ClientPriceElementFormState, {field, newValue}) {
        switch (field) {
            case 'hidden':
                if (!newValue) {
                    state.form['hidden_date'] = null;
                } else if (!state.form['hidden_date']) {
                    state.form['hidden_date'] = moment().toDate()
                }
                state.form[field] = newValue;
                break;
            default:
                state.form[field] = newValue;
        }
    },

    ADD_PRODUCT(state, product) {
      console.log(product)
  
      state.product_rels.push({
        client_product_container_rel_id: product.id,
        name: product.label
      })
    },

    REMOVE_PRODUCT(state, index) {
      console.log(state.product_rels)
      if (state.product_rels[index].id) {
        state.targetForDelete.push(state.product_rels[index].id)
      }
  
      state.product_rels.splice(index, 1);
    }
};

const getters = {
    fields: (state: ClientPriceElementFormState) => {
        return state.schema.fields.reduce((prev: any, field: any) => {
            prev[field.code] = field;
            return prev;
        }, {});
    },
};

export const STORE_KEY = '$_price_item_details_form';

export const store = {
    namespaced: true,
    state,
    actions,
    getters,
    mutations,
};
